import React from 'react'
import { CardFrame } from '../../../ui/CardFrame/CardFrame.jsx'
import { MiniPill } from '../../../ui/MiniPill/MiniPill.jsx'

/**
 * ProgressUpdatesCard — auto-populated from last session's Next priorities,
 * status per row (done / in-progress / stalled / dropped) + optional note.
 * catalog #19, prep-progress-updates.
 *
 * Auto-population from Phase H Carry edge: shown as empty "waiting" state
 * until Phase H lands. Phase E ships the card UI with an appropriate empty state.
 */

const STATUS_OPTIONS = ['done', 'in-progress', 'stalled', 'dropped']
const STATUS_TONE = { done: 'moss', 'in-progress': 'clay', stalled: 'mustard', dropped: 'rust' }

function StatusCycle({ value, onChange, disabled }) {
  function cycle() {
    if (disabled) return
    const idx = STATUS_OPTIONS.indexOf(value)
    const next = STATUS_OPTIONS[(idx + 1) % STATUS_OPTIONS.length]
    onChange(next)
  }

  if (!value) {
    return (
      <button
        type="button"
        onClick={cycle}
        disabled={disabled}
        style={styles.emptyStatus}
        aria-label="Set status"
      >
        + status
      </button>
    )
  }

  return (
    <MiniPill
      tone={STATUS_TONE[value] ?? 'clay'}
      as="button"
      type="button"
      onClick={cycle}
      disabled={disabled}
      aria-label={`Status: ${value}. Click to change.`}
      style={{ cursor: disabled ? 'default' : 'pointer', border: 'none', background: 'none' }}
    >
      {value}
    </MiniPill>
  )
}

export function ProgressUpdatesCard({ card, value, onChange, disabled }) {
  const items = value?.items ?? []
  const hasBullets = items.some((i) => i.text)

  if (!hasBullets) {
    return (
      <CardFrame
        kind="prep"
        icon="▸"
        iconClass="olive"
        title="Progress updates"
        prompt="What happened with last session's priorities?"
        state="empty"
        data-testid="prep-card-prep-progress-updates"
        className="prepcard"
      >
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>
            Items from last session&apos;s priorities will appear here automatically.
          </p>
          {/* Phase E follow-up: Phase H Carry edge will auto-populate this card */}
        </div>
      </CardFrame>
    )
  }

  function handleStatusChange(idx, status) {
    const next = items.map((item, i) => i === idx ? { ...item, status } : item)
    onChange({ items: next })
  }

  function handleNoteChange(idx, note) {
    const next = items.map((item, i) => i === idx ? { ...item, note } : item)
    onChange({ items: next })
  }

  return (
    <CardFrame
      kind="prep"
      icon="▸"
      iconClass="olive"
      title="Progress updates"
      prompt="What happened with last session's priorities?"
      state={disabled ? 'locked' : 'filled'}
      data-testid="prep-card-prep-progress-updates"
      className="prepcard"
    >
      <div style={styles.wrap}>
        {items.map((item, idx) => (
          <div key={item.id ?? idx} style={styles.row}>
            <div style={styles.bullet}>
              <span style={styles.prefix} aria-hidden>—</span>
              <span style={styles.text}>{item.text}</span>
              <StatusCycle
                value={item.status}
                onChange={(s) => handleStatusChange(idx, s)}
                disabled={disabled}
              />
            </div>
            {item.status && (
              <input
                type="text"
                value={item.note ?? ''}
                placeholder="any note?"
                disabled={disabled}
                onChange={(e) => handleNoteChange(idx, e.target.value)}
                style={styles.noteInput}
                aria-label={`Note for ${item.text}`}
                maxLength={120}
              />
            )}
          </div>
        ))}
      </div>
    </CardFrame>
  )
}

const styles = {
  emptyState: { padding: '16px 14px 20px', textAlign: 'center' },
  emptyText: { fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic', lineHeight: 1.5 },
  wrap: { padding: '8px 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  row: { display: 'flex', flexDirection: 'column', gap: 4 },
  bullet: { display: 'flex', alignItems: 'center', gap: 8 },
  prefix: { color: 'var(--color-text-faint)', fontSize: 12, flexShrink: 0 },
  text: { flex: 1, fontSize: 12, color: 'var(--color-text-primary)' },
  noteInput: {
    marginLeft: 16, border: 'none', borderBottom: '1px solid var(--color-line)',
    outline: 'none', background: 'transparent', fontSize: 11,
    color: 'var(--color-text-secondary)', fontFamily: 'inherit', padding: '1px 0', width: '100%',
  },
  emptyStatus: {
    fontSize: 9, fontWeight: 500, padding: '2px 6px',
    background: 'transparent', color: 'var(--color-text-muted)',
    border: '0.5px dashed var(--color-line)', borderRadius: 4,
    textTransform: 'uppercase', letterSpacing: '0.03em',
    cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit',
  },
}
